/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useCMSHeaderContent } from '@/features/header/api/getCMSHeaderContent';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useRef, useEffect } from 'react';

export const useIsSticky = (
  className: string,
  alertPresentClass: string,
  marginClassName: string,
  underRecsPerPage: boolean,
  resultsListRef?: React.RefObject<HTMLDivElement | null>,
) => {
  const mobileFiltersRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const { data: cmsHeaderData } = useCMSHeaderContent();
  const alertBannerEnabled =
    cmsHeaderData?.sitewideBanner && cmsHeaderData?.sitewideAlertBanner?.length;

  useEffect(() => {
    const alertBannerHeight = alertBannerEnabled ? (isMobile ? 65 : 70) : 0;
    const topheaderheight = (isMobile ? 64 : 67) + alertBannerHeight;
    const stickyFiltersAndHeaderHeight =
      (mobileFiltersRef?.current?.offsetHeight ?? 0) + topheaderheight;
    if (!mobileFiltersRef.current || !resultsListRef?.current) {
      return;
    }

    if (alertBannerEnabled) {
      mobileFiltersRef.current.classList.add(alertPresentClass);
    }
    const observer = new IntersectionObserver(
      ([event]) => {
        event.target.classList.toggle(
          className,
          event.intersectionRatio < 1 && event.intersectionRatio > 0.8,
        );
      },
      { threshold: [1, 0.96, 0.9, 0.8], rootMargin: `-${topheaderheight}px 0px 0px 0px` },
    );
    observer.observe(mobileFiltersRef.current);

    const resultsListObserver = new IntersectionObserver(
      ([event]) => {
        mobileFiltersRef.current?.classList.toggle(
          marginClassName,
          (event.intersectionRatio < 0.02 ||
            (underRecsPerPage && event.intersectionRatio < 0.05)) &&
            event.target.getBoundingClientRect().top <=
              mobileFiltersRef.current.getBoundingClientRect().bottom,
        );
      },
      {
        threshold: [1, 0.9],
        rootMargin: `-${stickyFiltersAndHeaderHeight}px 0px 0px 0px`,
      },
    );
    resultsListObserver.observe(resultsListRef.current);

    return () => {
      observer.disconnect();
      resultsListObserver.disconnect();
    };
  }, [
    className,
    underRecsPerPage,
    isMobile,
    marginClassName,
    resultsListRef,
    alertPresentClass,
    alertBannerEnabled,
  ]);

  return { mobileFiltersRef };
};
